import * as React from 'react';
import {FormattedMessage} from 'react-intl';

import {
    getCurrentLocale,
    getCurrentSite,
    getLocalizedKvm,
    getLocalizedPrice,
    getLocalizedRok,
} from 'features/services/utils/env-helper';
import {LOCALE, ParagraphSize, SITE, HeadlineLevel} from 'features/ui/enums';
import {ImageSizes} from '@organisms/Shell/image-size-enum';
import {DisposalForm, ObjectStatus} from 'features/search/filters/enums';
import {getFormattedMessage} from 'features/services/utils/language-helper';

import {P} from '@atoms/Text/P';
import {Icon} from '@atoms/Media/Icon';
import {AsyncImage} from '@atoms/Media/AsyncImage';
import {ResidenceTags} from '@atoms/Tags/ResidenceTags';
import {SearchResult} from "features/search/types";
import {Headline} from '@atoms/Text/Headline';

interface CardContentProps {
    object: SearchResult;
}

export const ResidenceExplorerObjectCard: React.FC<CardContentProps> = ({ object }) => {
    const renderTitle = () => {
        const area = getCurrentSite() === SITE.NO && object.jmnoBraInternal ?
            getLocalizedKvm(object.jmnoBraInternal) :
            getLocalizedKvm(object.primaryArea);
        return `${getLocalizedRok(object.rooms)}, ${area}`;
    };

    return (
        <>
            <ResidenceTags concept={object.concept} objectStatus={object.objectStatus} />
            {object.floorPlan && (
                <div className="selected-residence__floorplan">
                    <AsyncImage
                        fullHeight
                        src={object.floorPlan.url}
                        alt={`${getLocalizedRok(object.rooms)}, ${getLocalizedKvm(object.primaryArea)}`}
                        quality={ImageSizes.SIZE_1200}
                        noCrop
                    />
                </div>
            )}
            <span className="selected-residence__lgh-number">
                {object.objectStatus === ObjectStatus.SHOWING_APARTMENT ? (
                    <FormattedMessage id="jm.components.ResidenceTags.showingApartment" />
                ) : (
                    <span className="selected-residence__lgh">
                        {getFormattedMessage('jm.components.SelectedResidence.lgh')} {object.apartmentNumber}
                    </span>
                )}
            </span>
            <Headline level={HeadlineLevel.H5}>{renderTitle()}</Headline>
            {![ObjectStatus.SOLD, ObjectStatus.BOOKED, ObjectStatus.SHOWING_APARTMENT].includes(object.objectStatus) && (
                <P size={ParagraphSize.S}>
                    <span>
                        {`${getFormattedMessage('jm.components.ResidenceExplorer.price')} ${getLocalizedPrice(object.price)}`}
                    </span>
                    {getCurrentLocale() !== LOCALE.NO && object.fee && ![DisposalForm.OWNERSHIP_APARTMENT, DisposalForm.OWNERSHIP].includes(object.disposalForm as DisposalForm) && (
                        <span>
                            {`${getFormattedMessage('jm.components.ResidenceExplorer.fee')} ${getLocalizedPrice(object.fee)}`}
                        </span>
                    )}
                    {getCurrentLocale() === LOCALE.NO && object.jmnoSharedCost && object.disposalForm !== DisposalForm.CONDOMINIUM && (
                        <span>
                            {`${getFormattedMessage('jm.components.ResidenceExplorer.communityFee')} ${getLocalizedPrice(object.jmnoSharedCost)}`}
                        </span>
                    )}
                </P>
            )}
            {object.objectStatus !== ObjectStatus.SHOWING_APARTMENT && <Icon size={32} name="arrow-link" flip />}
        </>
    );
};
